import { useState, useCallback } from 'react';
import { upsellApi } from '../api/upsell';
import type { UpsellRecommendation, GenerateUpsellPayload, UpsellWidgetStatus } from '../api/upsell';

export function useUpsell() {
  const [recommendations, setRecommendations] = useState<UpsellRecommendation[]>([]);
  const [activeRecommendation, setActiveRecommendation] = useState<UpsellRecommendation | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [widgetStatus, setWidgetStatus] = useState<UpsellWidgetStatus | null>(null);
  const [refreshingWidget, setRefreshingWidget] = useState(false);

  const fetchRecommendations = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await upsellApi.list(p);
      setRecommendations(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load upsell recommendations.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateUpsellPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await upsellApi.generate(payload);
      const recommendation = res.data.data;
      setActiveRecommendation(recommendation);
      setRecommendations((prev) => [recommendation, ...prev]);
      return recommendation;
    } catch (err: any) {
      const msg = err?.message ?? 'Upsell recommendation generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteRecommendation = useCallback(async (id: number) => {
    try {
      await upsellApi.destroy(id);
      setRecommendations((prev) => prev.filter((r) => r.id !== id));
      if (activeRecommendation?.id === id) setActiveRecommendation(null);
    } catch {
      setError('Failed to delete recommendation.');
    }
  }, [activeRecommendation]);

  const fetchWidgetStatus = useCallback(async () => {
    try {
      const res = await upsellApi.widgetStatus();
      setWidgetStatus(res.data.data);
    } catch {
      // Non-critical — the widget status card just stays hidden/empty.
    }
  }, []);

  const refreshWidget = useCallback(async (payload: GenerateUpsellPayload = {}) => {
    setRefreshingWidget(true);
    setError(null);
    try {
      await upsellApi.refreshWidget(payload);
      await fetchWidgetStatus();
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Storefront widget refresh failed.');
      return false;
    } finally {
      setRefreshingWidget(false);
    }
  }, [fetchWidgetStatus]);

  return {
    recommendations, activeRecommendation, loading, generating, error, total, page, lastPage,
    fetchRecommendations, generate, deleteRecommendation, setActiveRecommendation, setError,
    widgetStatus, refreshingWidget, fetchWidgetStatus, refreshWidget,
  };
}
